import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Box,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { SavedView } from '../../types/ViewTypes';
import TextWithTooltip from '../Common/TextWithTooltip';

interface SavedViewsPanelProps {
  views: SavedView[];
  onViewClick: (view: SavedView) => void;
  onSaveClick: () => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, viewId: string) => void;
  activeView: SavedView | null;
  open: boolean;
  onClose: () => void;
}

const SavedViewsPanel: React.FC<SavedViewsPanelProps> = ({
  views,
  onViewClick,
  onMenuClick,
  activeView,
  open,
  onClose,
}) => {
  return (
    <Dialog
      data-testid="dialog-saved-views"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxHeight: '80vh',
          width: '400px',
          m: 'auto',
        },
      }}
    >
      <DialogTitle data-testid="dialog-saved-views-title" sx={{ p: 2, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            color="secondary"
            variant="subtitle1"
            sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
          >
            <AutoAwesomeIcon
              color="secondary"
              fontSize="small"
              sx={{ mr: 0.5 }}
            />
            Saved Views
          </Typography>
          <IconButton
            data-testid="btn-close-saved-views"
            size="small"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2, pt: 1 }}>
        <List
          data-testid="saved-views-list"
          dense
          sx={{ maxHeight: '400px', overflow: 'auto' }}
        >
          {(views || []).map(view => (
            <ListItem
              key={view.id}
              data-testid={`view-item-${view.id}`}
              secondaryAction={
                <IconButton
                  data-testid={`btn-view-menu-${view.id}`}
                  edge="end"
                  onClick={e => {
                    e.stopPropagation();
                    onMenuClick(e, view.id);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                cursor: 'pointer',
                bgcolor:
                  activeView?.id === view.id
                    ? 'action.selected'
                    : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 1,
                paddingRight: '40px',
              }}
              onClick={() => onViewClick(view)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AutoAwesomeIcon
                  fontSize="small"
                  color={activeView?.id === view.id ? 'primary' : 'action'}
                />
              </ListItemIcon>
              <ListItemText
                primary={<TextWithTooltip text={view.name} variant="body2" />}
              />
            </ListItem>
          ))}

          {(!views || views.length === 0) && (
            <ListItem>
              <ListItemText
                primary="No saved views"
                secondary="Create a view to save your search criteria"
                primaryTypographyProps={{ color: 'text.secondary' }}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default SavedViewsPanel;
